import { useEffect, useState } from "react";
import client, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, Plus, Check, ExternalLink, Loader2, BadgeCheck } from "lucide-react";
import { CloudIcon, LangIcon } from "@/components/brand-icons";
import { cn } from "@/lib/utils";

function RegistryRow({ r, onImport, busy }) {
  return (
    <li className="flex items-center gap-3 px-4 py-3" data-testid={`registry-row-${r.type}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-display text-sm font-semibold text-slate-900">{r.name}</span>
          <BadgeCheck className="h-3.5 w-3.5 text-sky-600" title="Official provider resource" />
          <span className="rounded-full bg-slate-100 px-2 py-0.5 font-mono-tight text-[10px] uppercase tracking-widest text-slate-500">{r.category}</span>
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-2 font-mono-tight text-[11px] text-slate-500">
          <span className="inline-flex items-center gap-1"><LangIcon language="terraform" className="h-3 w-3" /> {r.type}</span>
          {r.bicep_type && <span className="inline-flex items-center gap-1"><LangIcon language="bicep" className="h-3 w-3" /> {r.bicep_type}</span>}
          {r.subcategory && <span className="text-slate-400">· {r.subcategory}</span>}
          <a href={r.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-slate-400 hover:text-slate-900" title="Open the official documentation">
            docs <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </div>
      {r.in_catalog ? (
        <span className={cn("inline-flex items-center gap-1 font-mono-tight text-[11px]", r.deleted ? "text-rose-600" : "text-emerald-700")} data-testid={`registry-in-catalog-${r.type}`}>
          <Check className="h-3.5 w-3.5" /> {r.deleted ? "deleted" : "in catalog"}
        </span>
      ) : (
        <Button size="sm" disabled={busy === r.type} onClick={() => onImport(r)} data-testid={`registry-add-${r.type}`} className="h-8 bg-slate-900 text-white hover:bg-slate-800">
          {busy === r.type ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Plus className="mr-1.5 h-3.5 w-3.5" />} Add
        </Button>
      )}
    </li>
  );
}

export function RegistryImportDialog({ open, onOpenChange, cloud, onImported }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (!open) return;
    const q = query.trim();
    if (q.length < 2) {
      setResults(null);
      return;
    }
    const handle = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await client.get("/catalog/registry/search", { params: { cloud, q, limit: 30 } });
        setResults(data);
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [query, cloud, open]);

  const importOne = async (r) => {
    setBusy(r.type);
    try {
      const { data } = await client.post("/catalog/registry/import", { cloud, terraform_type: r.type });
      toast.success(`${data.name} added to the catalog`);
      setResults((list) => list.map((x) => (x.type === r.type ? { ...x, in_catalog: true, catalog_key: data.key } : x)));
      onImported(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-hidden p-0" data-testid="registry-dialog">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle className="flex items-center gap-2 font-display">
            <CloudIcon cloud={cloud} className="h-5 w-5" /> Import from the official {cloud.toUpperCase()} provider registry
          </DialogTitle>
          <DialogDescription>Search the Terraform Registry documentation and add any official resource to the catalog in one click. Bicep types are suggested for Azure.</DialogDescription>
        </DialogHeader>
        <div className="px-6">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder="redis, cosmos, load balancer, sqs…" data-testid="registry-search" className="h-10 border-slate-200 pl-9 font-mono-tight text-sm" />
            {loading && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-slate-400" />}
          </div>
        </div>
        <div className="max-h-[55vh] overflow-y-auto border-t border-slate-100" data-testid="registry-results">
          {results === null && <p className="px-6 py-8 text-center text-sm text-slate-500">Type at least two characters to search the registry.</p>}
          {results && results.length === 0 && !loading && <p className="px-6 py-8 text-center text-sm text-slate-500" data-testid="registry-empty">No official resource matches “{query}”.</p>}
          {results && results.length > 0 && (
            <ul className="divide-y divide-slate-100">
              {results.map((r) => <RegistryRow key={r.type} r={r} onImport={importOne} busy={busy} />)}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
